import { memo, useCallback, useEffect, useState } from 'react'
import { Box, Dialog, DialogContent, Slider } from '@mui/material'
import TuneIcon from '@mui/icons-material/Tune'
import SpeedIcon from '@mui/icons-material/Speed'
import GavelIcon from '@mui/icons-material/Gavel'
import BalanceIcon from '@mui/icons-material/Balance'
import ToggleOnIcon from '@mui/icons-material/ToggleOn'
import ContentCutIcon from '@mui/icons-material/ContentCut'
import SortIcon from '@mui/icons-material/Sort'
import CloseIcon from '@mui/icons-material/Close'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import CheckIcon from '@mui/icons-material/Check'
import toast from 'react-hot-toast'
import {
  TOKENS,
  FONT_MONO,
  FONT_SANS,
  PillButton,
  TogSwitch,
} from './SchedulerDesign'
import {
  ConfigurationSettings,
  useConfigurationSettings,
  useUpdateConfigurationSettings,
  usePlanConfigurationSettings,
  useUpdatePlanConfigurationSettings,
  useApplyDontStartBeforeStagger,
} from './Scheduler.data'
import {
  readStoredMultiWeek,
  HOURS_PER_WEEK,
} from './ScheduleControlPanel.view'

// ─── Fallback defaults ────────────────────────────────────────────────────────

const DEFAULTS: ConfigurationSettings = {
  pop_size: 100,
  generations: 20,
  early_stop: 5,
  seed: 42,
  mutation_prob: 0.4,
  crossover_prob: 0.9,
  mutation_rate_adaptivity: 0.5,
  tournament_size: 3,
  allow_cleaning_during_downtime: false,
  enable_resource_yield: false,
  allow_missing_base_material_changeover: true,
  allow_missing_product_changeover: true,
  w_changeover_cost: 0,
  w_changeover_env_cost: 0,
  // Hours of makespan per CIP avoided. 0 = off = historical behaviour; clean
  // count is already NSGA-II F2 without it. See SchedulerRunConfig.
  w_clean_count: 0,
  w_crew_constraint: 1,
  w_minrun: 1,
  w_late: 1,
  w_user_throughput: 0.25,
  w_user_service: 0.25,
  w_user_ops: 0.25,
  w_user_balance: 0.25,
  batch_product_order: 'Auto',
  tail_buffer_hours: 400,
  // Small-remainder demand adjustment gates. Only reached when the settings
  // fetch fails — `setDraft(configSettings ?? DEFAULTS)` otherwise hydrates
  // the whole persisted row. Values mirror the engine defaults in
  // scheduler_rust preprocess.rs, so a tenant that has never opened the
  // Small-run removal section below runs exactly as it did before it existed.
  small_batch_drop_hours: 2,
  small_batch_drop_max_fraction: 0.1,
  survey_probe_count: 60,
}

// ─── NumCell — labelled ± stepper (ports ConfigureRunDialog.jsx's NumCell) ────

const STEP_SX = {
  width: 26,
  height: '100%',
  border: 'none',
  p: 0,
  m: 0,
  bgcolor: TOKENS.line100,
  cursor: 'pointer',
  fontSize: 15,
  color: TOKENS.ink500,
  fontFamily: FONT_MONO,
  flexShrink: 0,
  lineHeight: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': { bgcolor: TOKENS.line200 },
} as const

const NumCell = memo(
  ({
    label,
    value,
    onChange,
    step = 1,
    min,
    max,
  }: {
    label: string
    value: number
    onChange: (v: number) => void
    step?: number
    min?: number
    max?: number
  }) => {
    const clamp = (v: number) => {
      let r = v
      if (min !== undefined) r = Math.max(min, r)
      if (max !== undefined) r = Math.min(max, r)
      return +r.toFixed(4)
    }
    return (
      <Box>
        <Box
          sx={{
            fontSize: 9.5,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: TOKENS.ink400,
            mb: '4px',
            fontFamily: FONT_SANS,
          }}
        >
          {label}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            border: `1px solid ${TOKENS.line300}`,
            borderRadius: '2px',
            overflow: 'hidden',
            height: 30,
            bgcolor: '#fff',
          }}
        >
          <Box
            component="button"
            onClick={() => onChange(clamp(value - step))}
            sx={STEP_SX}
          >
            −
          </Box>
          <Box
            component="input"
            type="number"
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const v = parseFloat(e.target.value)
              if (!isNaN(v)) onChange(clamp(v))
            }}
            sx={{
              // flex: 1 + width: 100% ensures the input fills the flex slot across all browsers
              flex: 1,
              width: '100%',
              minWidth: 0,
              border: 'none',
              borderLeft: `1px solid ${TOKENS.line200}`,
              borderRight: `1px solid ${TOKENS.line200}`,
              outline: 'none',
              textAlign: 'center',
              fontFamily: FONT_MONO,
              fontSize: 12.5,
              fontWeight: 600,
              color: TOKENS.ink900,
              bgcolor: 'transparent',
              height: '100%',
              fontVariantNumeric: 'tabular-nums',
              p: 0,
              m: 0,
              // Hide browser-native number-input spinners
              '&::-webkit-inner-spin-button': {
                WebkitAppearance: 'none',
                margin: 0,
              },
              '&::-webkit-outer-spin-button': {
                WebkitAppearance: 'none',
                margin: 0,
              },
              '&[type=number]': { MozAppearance: 'textfield' },
            }}
          />
          <Box
            component="button"
            onClick={() => onChange(clamp(value + step))}
            sx={STEP_SX}
          >
            +
          </Box>
        </Box>
      </Box>
    )
  }
)
NumCell.displayName = 'NumCell'

// ─── PriorityRow — slider with live value (ports ConfigureRunDialog.jsx's PriorityRow) ──

const PriorityRow = memo(
  ({
    label,
    hint,
    value,
    onChange,
  }: {
    label: string
    hint: string
    value: number
    onChange: (v: number) => void
  }) => (
    <Box sx={{ mb: '12px' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: '8px',
        }}
      >
        <Box
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: TOKENS.ink700,
            fontFamily: FONT_SANS,
          }}
        >
          {label}
        </Box>
        <Box
          sx={{
            fontFamily: FONT_MONO,
            fontSize: 12,
            fontWeight: 600,
            color: TOKENS.stateComplete,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {value.toFixed(2)}
        </Box>
      </Box>
      <Box
        sx={{
          fontSize: 10.5,
          color: TOKENS.ink400,
          lineHeight: 1.3,
          mb: '3px',
        }}
      >
        {hint}
      </Box>
      <Slider
        size="small"
        value={value}
        onChange={(_, v) => onChange(v as number)}
        min={0}
        max={1}
        step={0.05}
        valueLabelDisplay="off"
        sx={{
          py: '4px',
          color: TOKENS.brandBlue,
          '& .MuiSlider-thumb': { width: 12, height: 12 },
        }}
      />
    </Box>
  )
)
PriorityRow.displayName = 'PriorityRow'

// ─── ToggleRow — custom pill switch (ports ConfigureRunDialog.jsx's ToggleRow) ──

const ToggleRow = memo(
  ({
    on,
    onChange,
    label,
    desc,
    first,
  }: {
    on: boolean
    onChange: (v: boolean) => void
    label: string
    desc?: string
    first?: boolean
  }) => (
    <Box
      sx={{
        display: 'flex',
        gap: '11px',
        py: '11px',
        borderTop: first ? 'none' : `1px solid ${TOKENS.line100}`,
      }}
    >
      <TogSwitch on={on} onChange={onChange} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            fontSize: 12.5,
            fontWeight: 600,
            color: TOKENS.ink900,
            fontFamily: FONT_SANS,
          }}
        >
          {label}
        </Box>
        {desc && (
          <Box
            sx={{
              fontSize: 10.5,
              color: TOKENS.ink400,
              lineHeight: 1.35,
              mt: '1px',
            }}
          >
            {desc}
          </Box>
        )}
      </Box>
    </Box>
  )
)
ToggleRow.displayName = 'ToggleRow'

// ─── ChoiceGroup — one-of-N option list (radio semantics, ToggleRow styling) ──

type BatchProductOrder = ConfigurationSettings['batch_product_order']

const BATCH_ORDER_OPTIONS: {
  value: BatchProductOrder
  label: string
  desc: string
}[] = [
  {
    value: 'Auto',
    label: 'Auto',
    desc: 'The algorithm explores product orders and keeps the best it finds.',
  },
  {
    value: 'SmallestFirst',
    label: 'Smallest first',
    desc: 'Lowest volume (demand × conversion factor) to highest.',
  },
  {
    value: 'LargestFirst',
    label: 'Largest first',
    desc: 'Highest volume to lowest.',
  },
]

const ChoiceGroup = memo(
  ({
    value,
    onChange,
    label,
  }: {
    value: BatchProductOrder
    onChange: (v: BatchProductOrder) => void
    label: string
  }) => (
    <Box role="radiogroup" aria-label={label}>
      {BATCH_ORDER_OPTIONS.map((o, i) => {
        const on = o.value === value
        return (
          <Box
            key={o.value}
            role="radio"
            aria-checked={on}
            tabIndex={0}
            onClick={() => onChange(o.value)}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault()
                onChange(o.value)
              }
            }}
            sx={{
              display: 'flex',
              gap: '11px',
              py: '9px',
              cursor: 'pointer',
              borderTop: i === 0 ? 'none' : `1px solid ${TOKENS.line100}`,
              '&:focus-visible': {
                outline: 'none',
                boxShadow: '0 0 0 3px rgba(0,130,179,.28)',
              },
            }}
          >
            <Box
              sx={{
                width: 14,
                height: 14,
                mt: '2px',
                flexShrink: 0,
                borderRadius: '50%',
                border: `1.5px solid ${on ? TOKENS.stateComplete : TOKENS.line300}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {on && (
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: TOKENS.stateComplete,
                  }}
                />
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: TOKENS.ink900,
                  fontFamily: FONT_SANS,
                }}
              >
                {o.label}
              </Box>
              <Box
                sx={{
                  fontSize: 10.5,
                  color: TOKENS.ink400,
                  lineHeight: 1.35,
                  mt: '1px',
                }}
              >
                {o.desc}
              </Box>
            </Box>
          </Box>
        )
      })}
    </Box>
  )
)
ChoiceGroup.displayName = 'ChoiceGroup'

// ─── CfgSection — bordered card with teal section header ─────────────────────

const CfgSection = memo(
  ({
    icon,
    title,
    hint,
    children,
  }: {
    icon: React.ReactNode
    title: string
    hint?: string
    children: React.ReactNode
  }) => (
    <Box
      sx={{
        border: `1px solid ${TOKENS.line200}`,
        borderRadius: '2px',
        bgcolor: '#fff',
        p: '13px 14px',
        mb: '12px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '7px',
          pb: '8px',
          mb: '12px',
          borderBottom: `1.5px solid ${TOKENS.stateComplete}`,
        }}
      >
        <Box
          sx={{
            color: TOKENS.stateComplete,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {icon}
        </Box>
        <Box
          sx={{
            fontSize: 10.5,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            color: TOKENS.stateComplete,
            fontFamily: FONT_SANS,
          }}
        >
          {title}
        </Box>
        {hint && (
          <Box sx={{ ml: 'auto', fontSize: 10.5, color: TOKENS.ink300 }}>
            {hint}
          </Box>
        )}
      </Box>
      {children}
    </Box>
  )
)
CfgSection.displayName = 'CfgSection'

// ─── Main dialog ──────────────────────────────────────────────────────────────

export const ConfigurationSettingsDialog = memo(
  ({
    open,
    onClose,
    schedulerId = '',
    iteration = '0',
    selectedWeek = 1,
    onWeekChange,
  }: {
    open: boolean
    onClose: () => void
    schedulerId?: string
    iteration?: string
    /** Currently active week — shown in the selector so the user knows what will be scheduled. */
    selectedWeek?: number
    onWeekChange?: (w: number) => void
  }) => {
    // A schedulerId means this dialog was opened as "Configure run" from
    // inside a specific plan — reads/writes must stay scoped to that plan
    // and never touch the tenant-wide Configuration Settings row. Without
    // one (opened from the Scheduler page), it edits the tenant SOR.
    const isPlanScoped = Boolean(schedulerId)

    const { data: tenantSettings } = useConfigurationSettings()
    const { data: planSettingsResult } = usePlanConfigurationSettings(
      schedulerId,
      iteration,
      isPlanScoped
    )
    const configSettings = isPlanScoped
      ? planSettingsResult?.data
      : tenantSettings

    const [draft, setDraft] = useState<ConfigurationSettings>(DEFAULTS)
    const [saving, setSaving] = useState(false)

    const onSuccess = useCallback(() => {
      setSaving(false)
      toast.success(
        isPlanScoped
          ? 'Run settings saved for this plan'
          : 'Configuration settings saved'
      )
      onClose()
    }, [onClose, isPlanScoped])

    const onError = useCallback(() => {
      setSaving(false)
      toast.error('Failed to save settings')
    }, [])

    const { mutate: updateTenantSettings } = useUpdateConfigurationSettings(
      onSuccess,
      onError
    )
    const { mutate: updatePlanSettings } = useUpdatePlanConfigurationSettings(
      onSuccess,
      onError
    )

    useEffect(() => {
      if (open) {
        setDraft(configSettings ?? DEFAULTS)
        setSaving(false)
      }
    }, [open, configSettings])

    const set = useCallback(
      <K extends keyof ConfigurationSettings>(
        k: K,
        v: ConfigurationSettings[K]
      ) => {
        setDraft((p) => ({ ...p, [k]: v }))
      },
      []
    )

    // ─── Multi-week state machine (mirrors ScheduleControlPanel) ─────────────────
    const multiKey = `multiWeek_${schedulerId}_${iteration}`

    const [multiState, setMultiState] = useState<
      'off' | 'selecting' | 'applied'
    >(() => readStoredMultiWeek(multiKey)?.state ?? 'off')
    const [multiWeeks, setMultiWeeks] = useState<Set<number>>(
      () => new Set(readStoredMultiWeek(multiKey)?.weeks ?? [])
    )

    // Persist any change to localStorage immediately
    useEffect(() => {
      try {
        localStorage.setItem(
          multiKey,
          JSON.stringify({ state: multiState, weeks: Array.from(multiWeeks) })
        )
      } catch {
        /* silent */
      }
    }, [multiKey, multiState, multiWeeks])

    // Re-seed from localStorage when the dialog opens (e.g. after a run completed)
    useEffect(() => {
      if (open) {
        const stored = readStoredMultiWeek(multiKey)
        if (stored) {
          setMultiState(stored.state)
          setMultiWeeks(new Set(stored.weeks))
        }
      }
    }, [open, multiKey])

    const onStaggerSuccess = useCallback(() => {
      toast.success('Multi-week stagger applied')
    }, [])
    const onStaggerError = useCallback(() => {
      toast.error('Failed to apply stagger')
    }, [])
    const { mutate: applyStagger, isLoading: isApplyingStagger } =
      useApplyDontStartBeforeStagger(onStaggerSuccess, onStaggerError)

    const handleMultiWeekClick = useCallback(() => {
      if (multiState === 'off') {
        setMultiState('selecting')
        setMultiWeeks(new Set([selectedWeek]))
        return
      }
      if (multiState === 'selecting') {
        const weeks = Array.from(multiWeeks).sort((a, b) => a - b)
        if (weeks.length < 2) {
          setMultiState('off')
          return
        }
        const earliest = weeks[0]
        const weekToHour: Record<number, number | null> = {}
        weeks.forEach((w) => {
          weekToHour[w] = (w - earliest) * HOURS_PER_WEEK
        })
        applyStagger(
          { schedulerId, iteration, weekToHour },
          {
            onSuccess: () => {
              setMultiState('applied')
            },
            onError: () => {
              toast.error('Failed to apply multi-week stagger')
            },
          }
        )
        return
      }
      // applied → revert: clear dontStartBefore for ALL weeks
      const weekToHour: Record<number, number | null> = {}
      for (let w = 1; w <= 8; w++) weekToHour[w] = null
      applyStagger(
        { schedulerId, iteration, weekToHour },
        {
          onSuccess: () => {
            setMultiState('off')
            setMultiWeeks(new Set())
          },
          onError: () => {
            toast.error('Failed to clear multi-week stagger')
          },
        }
      )
    }, [
      multiState,
      multiWeeks,
      selectedWeek,
      applyStagger,
      schedulerId,
      iteration,
    ])

    const handleTileClick = useCallback(
      (w: number) => {
        if (multiState === 'selecting') {
          setMultiWeeks((prev) => {
            const next = new Set(prev)
            if (next.has(w)) next.delete(w)
            else next.add(w)
            return next
          })
        } else {
          onWeekChange?.(w)
        }
      },
      [multiState, onWeekChange]
    )

    const multiLabel =
      multiState === 'applied'
        ? `Applied (${multiWeeks.size})`
        : multiState === 'selecting' && multiWeeks.size > 0
          ? `Apply (${multiWeeks.size})`
          : 'Multi-week'

    const GRID3 = {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '11px',
      // Each grid cell must be able to shrink below its content's intrinsic min-width
      '& > *': { minWidth: 0 },
    } as const

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: 'min(740px, 94vw)',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            border: `1px solid ${TOKENS.line300}`,
            borderRadius: '3px',
            overflow: 'hidden',
            m: '24px',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '11px',
            px: '18px',
            py: '15px',
            borderBottom: `1px solid ${TOKENS.line200}`,
            flexShrink: 0,
            bgcolor: TOKENS.surface0,
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: '2px',
              bgcolor: TOKENS.tealTint,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <TuneIcon sx={{ fontSize: 18, color: TOKENS.stateComplete }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                fontSize: 15,
                fontWeight: 700,
                color: TOKENS.ink900,
                letterSpacing: '-0.01em',
                fontFamily: FONT_SANS,
              }}
            >
              Configure run
            </Box>
            <Box sx={{ fontSize: 11.5, color: TOKENS.ink400 }}>
              {isPlanScoped
                ? 'Run settings for this plan only — does not change the tenant defaults.'
                : 'Default run settings used when a new schedule is created.'}
            </Box>
          </Box>
          <Box
            component="button"
            onClick={onClose}
            sx={{
              border: 'none',
              bgcolor: 'transparent',
              cursor: 'pointer',
              p: '4px',
              borderRadius: '2px',
              display: 'flex',
              '&:hover': { bgcolor: TOKENS.surface50 },
            }}
          >
            <CloseIcon sx={{ fontSize: 18, color: TOKENS.ink400 }} />
          </Box>
        </Box>

        {/* Week selector + multi-week */}
        <Box
          sx={{
            px: '18px',
            py: '10px',
            borderBottom: `1px solid ${TOKENS.line200}`,
            bgcolor: TOKENS.surface0,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: '7px' }}>
            <Box
              sx={{
                fontSize: 9,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.07em',
                color: TOKENS.ink400,
                flex: 1,
                fontFamily: FONT_SANS,
              }}
            >
              {multiState === 'off' ? 'Week to schedule' : 'Weeks to schedule'}
            </Box>
            {/* Multi-week toggle */}
            <Box
              component="button"
              onClick={handleMultiWeekClick}
              disabled={isApplyingStagger}
              sx={{
                border: `1px solid ${multiState !== 'off' ? TOKENS.stateComplete : TOKENS.line300}`,
                borderRadius: '2px',
                bgcolor:
                  multiState === 'applied'
                    ? 'rgba(14,124,134,0.08)'
                    : multiState === 'selecting'
                      ? 'rgba(14,124,134,0.05)'
                      : '#fff',
                px: '8px',
                height: 22,
                cursor: 'pointer',
                fontSize: 10,
                fontWeight: 600,
                color:
                  multiState !== 'off' ? TOKENS.stateComplete : TOKENS.ink500,
                fontFamily: FONT_SANS,
                '&:disabled': { opacity: 0.5, cursor: 'default' },
              }}
            >
              {isApplyingStagger ? '…' : multiLabel}
            </Box>
          </Box>

          {/* Tile row */}
          <Box sx={{ display: 'flex', gap: '5px' }}>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((w) => {
              const inMulti = multiWeeks.has(w)
              const isSingle = w === selectedWeek
              // Highlight: in selecting/applied mode use multi set; otherwise single week
              const highlight = multiState !== 'off' ? inMulti : isSingle

              const borderColor = highlight ? TOKENS.brandTeal : TOKENS.line300
              const bgColor = highlight ? TOKENS.tealTint : '#fff'
              const textColor = highlight ? TOKENS.brandTeal : TOKENS.ink500

              return (
                <Box
                  key={w}
                  component="button"
                  onClick={() => handleTileClick(w)}
                  sx={{
                    border: `${highlight ? 2 : 1}px solid ${borderColor}`,
                    borderRadius: '2px',
                    bgcolor: bgColor,
                    px: highlight ? '7px' : '8px',
                    py: highlight ? '3px' : '4px',
                    cursor: 'pointer',
                    fontFamily: FONT_MONO,
                    fontSize: 11.5,
                    fontWeight: 700,
                    color: textColor,
                    transition: 'all 0.12s',
                    '&:hover': {
                      borderColor: TOKENS.brandTeal,
                      color: TOKENS.brandTeal,
                    },
                  }}
                >
                  W{w}
                </Box>
              )
            })}
          </Box>
          {multiState === 'selecting' && (
            <Box
              sx={{
                mt: '6px',
                fontSize: 10,
                color: TOKENS.ink400,
                fontFamily: FONT_SANS,
              }}
            >
              Click weeks to add/remove, then click{' '}
              <strong>Apply ({multiWeeks.size})</strong> to write the stagger.
            </Box>
          )}
          {multiState === 'applied' && (
            <Box
              sx={{
                mt: '6px',
                fontSize: 10,
                color: TOKENS.stateComplete,
                fontFamily: FONT_SANS,
              }}
            >
              Stagger applied across weeks{' '}
              {Array.from(multiWeeks)
                .sort((a, b) => a - b)
                .join(', ')}
              . Click <strong>Applied ({multiWeeks.size})</strong> to revert.
            </Box>
          )}
        </Box>

        {/* Scrollable body */}
        <DialogContent
          sx={{
            p: '14px 18px',
            bgcolor: TOKENS.surface50,
            minHeight: 0,
            overflowX: 'hidden',
          }}
        >
          <CfgSection
            icon={<SpeedIcon sx={{ fontSize: 15 }} />}
            title="Genetic algorithm"
            hint="Solver search behaviour"
          >
            <Box sx={GRID3}>
              <NumCell
                label="Population size"
                value={draft.pop_size}
                onChange={(v) => set('pop_size', v)}
                step={50}
                min={50}
              />
              <NumCell
                label="Generations"
                value={draft.generations}
                onChange={(v) => set('generations', v)}
                step={5}
                min={5}
              />
              <NumCell
                label="Early stop"
                value={draft.early_stop}
                onChange={(v) => set('early_stop', v)}
                step={1}
                min={1}
              />
              <NumCell
                label="Seed"
                value={draft.seed}
                onChange={(v) => set('seed', v)}
                step={1}
                min={0}
              />
              <NumCell
                label="Mutation prob"
                value={draft.mutation_prob}
                onChange={(v) => set('mutation_prob', v)}
                step={0.05}
                min={0}
                max={1}
              />
              <NumCell
                label="Crossover prob"
                value={draft.crossover_prob}
                onChange={(v) => set('crossover_prob', v)}
                step={0.05}
                min={0}
                max={1}
              />
              <NumCell
                label="Mutation adaptivity"
                value={draft.mutation_rate_adaptivity}
                onChange={(v) => set('mutation_rate_adaptivity', v)}
                step={0.1}
                min={0}
              />
              <NumCell
                label="Tournament size"
                value={draft.tournament_size}
                onChange={(v) => set('tournament_size', v)}
                step={1}
                min={2}
              />
              <NumCell
                label="Tail buffer (h)"
                value={draft.tail_buffer_hours}
                onChange={(v) => set('tail_buffer_hours', v)}
                step={50}
                min={0}
              />
              {/* Prepare Data's breadth. More probes sample more structural
                  basins, which is where most of the final makespan is
                  decided, at roughly proportional extra Prepare time.
                  0 skips the survey: Run refines straight from the
                  feasibility check's best schedule. */}
              <NumCell
                label="Survey probes"
                value={draft.survey_probe_count ?? 60}
                onChange={(v) => set('survey_probe_count', v)}
                step={10}
                min={0}
              />
            </Box>
          </CfgSection>

          <CfgSection
            icon={<GavelIcon sx={{ fontSize: 15 }} />}
            title="Rule strictness"
            hint="Constraint penalty weights"
          >
            <Box sx={GRID3}>
              <NumCell
                label="Crew constraint"
                value={draft.w_crew_constraint}
                onChange={(v) => set('w_crew_constraint', v)}
                step={0.5}
                min={0}
              />
              <NumCell
                label="Min run length"
                value={draft.w_minrun}
                onChange={(v) => set('w_minrun', v)}
                step={0.5}
                min={0}
              />
              <NumCell
                label="Late penalty"
                value={draft.w_late}
                onChange={(v) => set('w_late', v)}
                step={0.5}
                min={0}
              />
              <NumCell
                label="Changeover cost"
                value={draft.w_changeover_cost}
                onChange={(v) => set('w_changeover_cost', v)}
                step={0.1}
                min={0}
              />
              <NumCell
                label="Env cost"
                value={draft.w_changeover_env_cost}
                onChange={(v) => set('w_changeover_env_cost', v)}
                step={0.1}
                min={0}
              />
              <NumCell
                label="Clean count (hrs/clean)"
                value={draft.w_clean_count}
                onChange={(v) => set('w_clean_count', v)}
                step={0.1}
                min={0}
              />
            </Box>
          </CfgSection>

          {/* Restored 2026-10-30: the July UI port dropped this control but
              kept the field, so no planner could change it since. The engine
              (BatchProductOrder, applied in simplified_decoder) locks the
              order inside a batch for the two fixed choices; Auto leaves it
              to the search. */}
          <CfgSection
            icon={<SortIcon sx={{ fontSize: 15 }} />}
            title="Product order within a batch"
            hint="Which SKU runs first in a kitchen batch"
          >
            <ChoiceGroup
              label="Product order within a batch"
              value={draft.batch_product_order ?? 'Auto'}
              onChange={(v) => set('batch_product_order', v)}
            />
          </CfgSection>

          {/* Deliberately its own section rather than another cell in "Rule
              strictness". Every weight above steers the SEARCH; these two
              delete customer demand outright, and the engine credits what
              they delete as MET, so nothing downstream reports it as unmet.
              Filing them beside the penalty weights is what kept them
              invisible. Both gates must pass for a leftover to be dropped. */}
          <CfgSection
            icon={<ContentCutIcon sx={{ fontSize: 15 }} />}
            title="Small-run removal"
            hint="Deletes demand — read the note"
          >
            <Box sx={GRID3}>
              <NumCell
                label="Leftover under (hrs)"
                value={draft.small_batch_drop_hours}
                onChange={(v) => set('small_batch_drop_hours', v)}
                step={0.5}
                min={0}
              />
              <NumCell
                label="And under (share of run)"
                value={draft.small_batch_drop_max_fraction}
                onChange={(v) => set('small_batch_drop_max_fraction', v)}
                step={0.05}
                min={0}
                max={1}
              />
            </Box>
            <Box
              sx={{
                mt: '10px',
                fontSize: 10.5,
                lineHeight: 1.45,
                color: TOKENS.ink500,
                fontFamily: FONT_SANS,
              }}
            >
              When a kitchen max-run boundary splits a base&apos;s production, a
              leftover under <strong>both</strong> gates is dropped instead of
              opening a fresh batch and paying its clean. The dropped quantity
              is reported as <strong>met, not unmet</strong>, so raising either
              value discards demand without showing up anywhere as a shortfall —
              whatever fires is listed under the run results. The share gate
              exists because hours alone once discarded a third of a
              product&apos;s order as a &ldquo;rounding remainder&rdquo;. Set
              hours to <strong>0</strong> to switch the rule off.
            </Box>
          </CfgSection>

          <CfgSection
            icon={<BalanceIcon sx={{ fontSize: 15 }} />}
            title="Business priorities"
            hint="Rank the Pareto set"
          >
            <PriorityRow
              label="Throughput"
              hint="Favour schedules that finish production sooner."
              value={draft.w_user_throughput}
              onChange={(v) => set('w_user_throughput', v)}
            />
            <PriorityRow
              label="Service"
              hint="Favour schedules that meet demand on time."
              value={draft.w_user_service}
              onChange={(v) => set('w_user_service', v)}
            />
            <PriorityRow
              label="Ops / cost"
              hint="Favour fewer changeovers and lower cost."
              value={draft.w_user_ops}
              onChange={(v) => set('w_user_ops', v)}
            />
            <PriorityRow
              label="Balance"
              hint="Favour even workload across lines."
              value={draft.w_user_balance}
              onChange={(v) => set('w_user_balance', v)}
            />
          </CfgSection>

          <CfgSection
            icon={<ToggleOnIcon sx={{ fontSize: 15 }} />}
            title="Feature toggles"
          >
            <ToggleRow
              first
              on={draft.allow_cleaning_during_downtime}
              onChange={(v) => set('allow_cleaning_during_downtime', v)}
              label="Allow cleaning during downtime"
            />
            <ToggleRow
              on={draft.enable_resource_yield}
              onChange={(v) => set('enable_resource_yield', v)}
              label="Fragment production into capped-resource gaps"
              desc="On: schedule additional production in capped-resource free windows that the main pass missed."
            />
            <ToggleRow
              on={draft.allow_missing_base_material_changeover}
              onChange={(v) => set('allow_missing_base_material_changeover', v)}
              label="Allow missing base-material changeover"
              desc="On: missing rule = free transition. Off: drop the product from the schedule."
            />
            <ToggleRow
              on={draft.allow_missing_product_changeover}
              onChange={(v) => set('allow_missing_product_changeover', v)}
              label="Allow missing product changeover"
              desc="On: missing rule = free transition. Off: drop the product from the schedule."
            />
          </CfgSection>
        </DialogContent>

        {/* Footer */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            px: '18px',
            py: '12px',
            borderTop: `1px solid ${TOKENS.line200}`,
            flexShrink: 0,
            bgcolor: TOKENS.surface0,
          }}
        >
          <PillButton
            kind="ghost"
            icon={<RestartAltIcon />}
            onClick={() => setDraft(configSettings ?? DEFAULTS)}
          >
            Reset to defaults
          </PillButton>
          <Box sx={{ flex: 1 }} />
          <PillButton kind="subtle" onClick={onClose}>
            Cancel
          </PillButton>
          <PillButton
            kind="primary"
            disabled={saving}
            icon={<CheckIcon />}
            onClick={() => {
              setSaving(true)
              if (isPlanScoped) {
                updatePlanSettings({ schedulerId, iteration, settings: draft })
              } else {
                updateTenantSettings(draft)
              }
            }}
          >
            {saving ? 'Saving…' : 'Save & exit'}
          </PillButton>
        </Box>
      </Dialog>
    )
  }
)
ConfigurationSettingsDialog.displayName = 'ConfigurationSettingsDialog'
